import { useEffect, useRef, useState, useCallback } from "react";
import dayjs from "dayjs";

import { ChatType } from "../types";
import { useCaseViewContext } from '../../../context';

const getFullName = (messageData) => {
  const firstName = messageData.chatOwnerDetails?.firstName || messageData.firstName || '';
  const lastName = messageData.chatOwnerDetails?.lastName || messageData.lastName || '';
  return `${firstName} ${lastName}`.trim();
};

const getPatientName = (caseDetails, messageData) => {
  return caseDetails?.firstName || 
         caseDetails?.patient?.firstName || 
         messageData.firstName || 
         messageData.chatOwnerDetails?.firstName || 
         messageData.authorName || 
         'Patient';
};

export const useMessageList = ({
  chatContext,
  messages,
  handleMarkTicketAsRead,
}) => {
  const {
    caseDetails,
    provider,
    physicianDetails,
    apiType,
    apiClasses,
  } = useCaseViewContext();

  const messageContainerRef = useRef(null);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    if (apiType === 'admin') {
      try {
        const user = apiClasses.AuthorizationAPI.getUserDetails();
        setAdminUser(user);
      } catch (error) {
        console.error('Failed to fetch admin user details:', error);
        setAdminUser(null);
      }
    }
  }, [apiType, apiClasses]);

  useEffect(() => {
    const { current } = messageContainerRef;
    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  }, [messages.list]);

  const isProviderOwnMessage = useCallback((messageAuthorId, messageAuthorEmail) => {
    if (physicianDetails?.userId === messageAuthorId) {
      return true;
    }
    const providerEmail = provider?.email || provider?.userEmail;
    return providerEmail === messageAuthorEmail;
  }, [physicianDetails?.userId, provider?.email, provider?.userEmail]);

  const createChatMessageProps = useCallback((messageData) => {
    const messageAuthorId = messageData.author || messageData.chatOwnerDetails?._id;
    const messageAuthorEmail = messageData.chatOwnerDetails?.email;
    
    let isOwnMessage = false;
    let authorName;
    
    if (chatContext === ChatType.ADMIN) {
      if (apiType === 'admin') {
        if (adminUser) {
          isOwnMessage = adminUser.userEmail === messageAuthorEmail;
        }
      } else {
        isOwnMessage = isProviderOwnMessage(messageAuthorId, messageAuthorEmail);
      }

      authorName = isOwnMessage ? 'You' : getFullName(messageData) || 'Unknown';
    } else {
      if (apiType === 'admin') {
        const isPatientMessage = messageData.isPatient === true;
        isOwnMessage = !isPatientMessage;
        
        if (isPatientMessage) {
          authorName = getPatientName(caseDetails, messageData);
        } else {
          const isMyMessage = adminUser && adminUser.userEmail === messageAuthorEmail;
          authorName = isMyMessage ? 'You' : getFullName(messageData) || 'Provider';
        }
      } else {
        isOwnMessage = isProviderOwnMessage(messageAuthorId, messageAuthorEmail);
        
        if (isOwnMessage) {
          authorName = 'You';
        } else if (messageData.isPatient) {
          authorName = getPatientName(caseDetails, messageData);
        } else {
          authorName = getFullName(messageData) || messageData.authorName || 'Provider';
        }
      }
    }

    const isUnreadByChatType = {
      [ChatType.PATIENT]: () => !isOwnMessage && !messageData.read,
      [ChatType.ADMIN]: () => !isOwnMessage && !messageData.readBy?.includes("physician"),
    };

    const isUnread = isUnreadByChatType[chatContext]?.() || false;

    return {
      key: messageData._id,
      timestamp: dayjs(messageData.created),
      handleMarkTicketAsRead,
      isOwnMessage,
      isUnread,
      contentSrc: messageData.key,
      message: messageData.message,
      authorName,
      isPatient: messageData.isPatient,
      isSending: messageData.isSending || false,
      apiType,
    };
  }, [chatContext, apiType, adminUser, physicianDetails, provider, caseDetails, handleMarkTicketAsRead, isProviderOwnMessage]);

  return {
    caseDetails,
    messageContainerRef,
    createChatMessageProps,
  };
};
